

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader } from '@/shared/ui/card';
import { getDailyActivitiesForProject } from '@/features/projects/api';
import { projectRepository } from '@/features/projects/repository';
import { DailyActivityWithAssignments, Project, ProjectStatus, ProjectZone, Craftsman, CompanyVehicle, Truck, TruckDriver } from '@/types';
import { ProjectWithZones } from '@/features/projects/types';
import { Loader2, Save } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from "sonner";
import { mutate } from 'swr';
import { BasicInfoSection } from './BasicInfoSection';
import { DailyActivitySection } from './DailyActivitySection';
import { ProjectSectionHeader } from './ProjectSectionHeader';
import { ResourceEditPopup } from './ResourceEditPopup';
import { ZoneListSection } from './ZoneListSection';

/**
 * 拡張したProjectWithZonesの型定義
 */
interface ExtendedProject extends ProjectWithZones {
  client_name?: string;
  location?: string;
  description?: string;
}

/**
 * プロジェクトセクションのプロパティ
 */
interface ProjectSectionProps {
  project: ExtendedProject;
  isReadOnly: boolean;
  craftsmen: Craftsman[] | null;
  companyVehicles: CompanyVehicle[] | null;
  trucks: Truck[] | null;
  truckDrivers: TruckDriver[] | null;
  currentUserCraftsmanId?: string | null;
  onProjectUpdated?: () => void; // 親コンポーネントへの更新通知
  onScheduleUpdated?: () => void; // 個人スケジュール更新通知
}

/**
 * プロジェクトセクション
 * @param param0 プロジェクトセクションのプロパティ
 * @returns プロジェクトセクション
 */
export const ProjectSection: React.FC<ProjectSectionProps> = ({
  project,
  isReadOnly,
  craftsmen,
  companyVehicles,
  trucks,
  truckDrivers,
  // materials: _materials, // 削除済み（GenbaFlowでは使用しない）
  currentUserCraftsmanId,
  onProjectUpdated,
  onScheduleUpdated,
}) => {
  // 状態管理
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // 編集中のデータ
  const [editedProject, setEditedProject] = useState<Partial<Project>>({});
  const [editedZones, setEditedZones] = useState<ProjectZone[]>(project.project_zones || []);
  const [zonesToDelete, setZonesToDelete] = useState<string[]>([]);
  // nullの場合はデフォルト値を設定
  const [editedStatus, setEditedStatus] = useState<ProjectStatus>(
    (project.status as ProjectStatus) || '進行中'
  );

  // 日次活動データ
  const [currentYearMonth, setCurrentYearMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dailyActivities, setDailyActivities] = useState<DailyActivityWithAssignments[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);

  // リソース編集ポップアップ
  const [resourcePopupOpen, setResourcePopupOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);

  // project_zonesの変更を監視し、editedZonesを更新
  useEffect(() => {
    if (!hasChanges) {
      setEditedZones(project.project_zones || []);
    }
  }, [project.project_zones, hasChanges]);

  // 基本情報の変更処理
  const handleBasicInfoChange = useCallback((updatedInfo: Partial<Project>) => {
    setEditedProject(prev => ({ ...prev, ...updatedInfo }));
    setHasChanges(true);
  }, []);

  // 工区の変更処理
  const handleZonesChange = useCallback((updatedZones: ProjectZone[]) => {
    // 削除された工区を検出
    const currentZoneIds = editedZones.map(zone => zone.id).filter(Boolean);
    const updatedZoneIds = updatedZones.map(zone => zone.id).filter(Boolean);
    const deletedZoneIds = currentZoneIds.filter(id => !updatedZoneIds.includes(id));

    // 削除された工区をzonesToDeleteに追加
    if (deletedZoneIds.length > 0) {
      setZonesToDelete(prev => [...prev, ...deletedZoneIds]);
    }

    setEditedZones(updatedZones);
    setHasChanges(true);
  }, [editedZones]);


  // ステータスの変更処理
  const handleStatusChange = useCallback((status: ProjectStatus) => {
    setEditedStatus(status);
    setHasChanges(true);
  }, []);

  // 年月の変更処理（日次活動データの再取得）
  const handleYearMonthChange = useCallback((yearMonth: string) => {
    // "YYYY-MM"形式のみを許容
    const [y, m] = yearMonth.split('-');
    setCurrentYearMonth(`${y}-${String(Number(m)).padStart(2, '0')}`);
  }, []);

  // リソース割り当てポップアップの表示
  const handleResourceEditRequest = useCallback((date: string, zoneId: string | null) => {
    console.log('ProjectSection handleResourceEditRequest called:', { date, zoneId });
    setSelectedDate(date);
    setSelectedZoneId(zoneId);
    setResourcePopupOpen(true);
    console.log('Resource popup should be open now');
  }, []);

  // 日次活動データの取得 - 並行処理最適化版
  const fetchDailyActivities = useCallback(async () => {
    if (!project.id) return;

    setLoadingActivities(true);
    setActivitiesError(null);

    try {
      const [yearStr, monthStr] = currentYearMonth.split('-');
      let processedMonthStr = monthStr;
      if (processedMonthStr && processedMonthStr.length > 2) {
        // 例: monthStr = "05-30" の場合
        processedMonthStr = processedMonthStr.slice(0, 2);
      }
      const year = Number(yearStr);
      const month = Number(processedMonthStr);

      // 月初・月末日付を計算
      const endDate = new Date(year, month, 0); // 月末日

      // タイムゾーンのずれを避けて正しい日付文字列を生成
      const formattedStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const formattedEndDate = `${year}-${String(month).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      // プロジェクト工区のIDリスト（表示データとの比較用）
      const projectZoneIds = (project.project_zones || []).map(z => String(z.id));

      // 複数のAPIリクエストを並行実行（N+1問題回避）
      const activitiesResponse = await getDailyActivitiesForProject(project.id, {
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });

      if (activitiesResponse.error) {
        throw new Error(activitiesResponse.error.message || '日次活動データの取得に失敗しました');
      }
      const activities = activitiesResponse.data || [];

      // ゾーン情報のチェック（プロジェクト固有のアクティビティのみ）
      const projectActivities = activities.filter(a => a.project_id === project.id);
      const activityZoneIds = [...new Set(projectActivities
        .filter(a => a.zone_id)
        .map(a => String(a.zone_id as string)))];

      // 工区ID不一致の警告（プロジェクト固有のアクティビティのみ）
      for (const zoneId of activityZoneIds) {
        if (!projectZoneIds.includes(zoneId)) {
          console.warn(`警告: プロジェクト ${project.name} のアクティビティに含まれる工区ID ${zoneId} はプロジェクト工区リストにありません`);
        }
      }

      // stateを更新後、少し遅延してからMatrixCalendarの再レンダリングを促す
      console.log('ProjectSection fetchDailyActivities - setting activities:', activities.length, 'activities');
      setDailyActivities(activities);

    } catch (error) {
      console.error('Error fetching daily activities:', error);
      setActivitiesError(error instanceof Error ? error.message : '日次活動データの取得に失敗しました');
    } finally {
      setLoadingActivities(false);
    }
  }, [project.id, project.project_zones, currentYearMonth, getDailyActivitiesForProject]);

  // 年月変更時に日次活動データを取得
  useEffect(() => {
    fetchDailyActivities();
  }, [fetchDailyActivities]);

  // 日次活動が更新された時のハンドラー
  const handleActivityUpdated = useCallback(async () => {
    console.log('ProjectSection handleActivityUpdated called - refreshing daily activities');
    try {
      await fetchDailyActivities();
      console.log('ProjectSection daily activities refreshed successfully');
      // 個人スケジュールも更新
      if (onScheduleUpdated) {
        onScheduleUpdated();
      }
    } catch (error) {
      console.error('ProjectSection handleActivityUpdated error:', error);
    }
  }, [fetchDailyActivities, onScheduleUpdated]);

  // 編集モード切り替え
  const toggleEditMode = useCallback(() => {
    if (editMode && hasChanges) {
      // 保存を促すメッセージを表示する可能性あり
      if (confirm('変更が保存されていません。保存せずに編集モードを終了しますか？')) {
        setEditMode(false);
        setHasChanges(false);
        // 編集データをリセット
        setEditedProject({});
        setEditedZones(project.project_zones || []);
        setZonesToDelete([]);
        setEditedStatus((project.status as ProjectStatus) || '進行中');
      }
    } else {
      setEditMode(!editMode);
      if (!editMode) {
        // 編集モードに入る時、現在のデータをセット
        setEditedZones(project.project_zones || []);
      }
    }
  }, [editMode, hasChanges, project]);

  // 保存処理
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    setError(null);

    try {
      // プロジェクト基本情報の更新（ステータス含む）
      const projectUpdatePayload = {
        ...editedProject,
        status: editedStatus,
        updated_by: '00000000-0000-0000-0000-00000000a001'
      };

      console.log('ProjectSection handleSave - editedProject:', editedProject);
      console.log('ProjectSection handleSave - projectUpdatePayload:', projectUpdatePayload);

      if (Object.keys(projectUpdatePayload).length > 0) {
        await projectRepository.updateProject(project.id, projectUpdatePayload);
      }

      // 工区情報の更新
      // 1. 既存工区の更新
      const existingZones = editedZones.filter(zone => {
        // 既存の工区IDリストに含まれている工区を既存として扱う
        const existingZoneIds = (project.project_zones || []).map(z => z.id);
        return existingZoneIds.includes(zone.id);
      });
      for (const zone of existingZones) {
        const zoneUpdated = await projectRepository.updateProjectZone(zone.id, {
          name: zone.zone_name,
          area_sqm: zone.area,
          notes: zone.notes
        });

        if (!zoneUpdated) {
          throw new Error(`工区 ${zone.zone_name} の更新に失敗しました`);
        }
      }

      // 2. 新規工区の追加
      const newZones = editedZones.filter(zone => {
        // 既存の工区IDリストに含まれていない工区を新規として扱う
        const existingZoneIds = (project.project_zones || []).map(z => z.id);
        return !existingZoneIds.includes(zone.id);
      });
      for (const zone of newZones) {
        await projectRepository.createProjectZone(project.id, {
          name: zone.zone_name || '未設定',
          area_sqm: zone.area ?? null,
          notes: zone.notes ?? null,
          tenant_id: 'default', // tenant_idはバックエンドで設定される
        } as Omit<ProjectZone, 'id' | 'project_id' | 'created_at' | 'updated_at'>);
      }

      // 3. 削除する工区の処理
      for (const zoneId of zonesToDelete) {
        if (zoneId) {
          try {
            // 関連する日次活動をチェックして強制削除
            await projectRepository.deleteProjectZone(zoneId);
          } catch (deleteError) {
            // 削除エラーの場合、より詳細なエラーメッセージを表示
            const errorMessage = deleteError instanceof Error
              ? deleteError.message
              : '工区の削除に失敗しました';

            // 外部キー制約エラーの場合、分かりやすいメッセージに変換
            if (errorMessage.includes('23503') || errorMessage.includes('violates foreign key constraint')) {
              throw new Error('この工区には関連する日次活動があるため削除できません。先に関連する日次活動を削除してください。');
            }

            throw new Error(`工区削除エラー: ${errorMessage}`);
          }
        }
      }

      // キャッシュを更新（プロジェクト関連のすべてのキャッシュ）
      await Promise.all([
        // プロジェクト一覧のキャッシュを更新
        mutate(
          (key) => typeof key === 'string' && key.startsWith('/projects'),
          undefined,
          { revalidate: true }
        ),
        // 日次活動のキャッシュを更新
        mutate(
          (key) => typeof key === 'string' && key.startsWith('/daily-activities'),
          undefined,
          { revalidate: true }
        ),
        // REMOVED: project-material-usages cache excluded from GenbaFlow
      ]);

      // 保存成功
      toast.success("保存完了", {
        description: "プロジェクト情報が正常に保存されました",
      });

      // 状態のリセット
      setHasChanges(false);
      setEditMode(false);

      // 親コンポーネントに更新を通知
      if (onProjectUpdated) {
        console.log('ProjectSection handleSave - 親コンポーネントに更新を通知');
        await onProjectUpdated();
        console.log('ProjectSection handleSave - 親コンポーネントの更新通知完了');
      }

      // 状態が変わった場合の処理
      if (editedStatus !== project.status && editedStatus !== '進行中') {
        // 進行中でなくなった場合、リストから削除する必要があるかも
        // この場合は親コンポーネントに通知する仕組みが必要
      }

    } catch (err) {
      console.error('Error saving project:', err);
      setError(err instanceof Error ? err.message : '保存中にエラーが発生しました');
      toast.error("保存エラー", {
        description: err instanceof Error ? err.message : '保存中にエラーが発生しました',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 overflow-hidden border shadow-md hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="bg-muted/30 p-4">
        <ProjectSectionHeader
          project={project}
          isReadOnly={isReadOnly}
          editMode={editMode}
          onEditToggle={toggleEditMode}
          onStatusChange={handleStatusChange}
          currentStatus={editedStatus}
        />
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* 基本情報セクション */}
        <BasicInfoSection
          project={project}
          isReadOnly={isReadOnly || !editMode}
          onChange={handleBasicInfoChange}
        />

        {/* 工区情報セクション */}
        <ZoneListSection
          zones={editedZones}
          onZoneChange={handleZonesChange}
          onResourceEditRequest={handleResourceEditRequest}
          isEditMode={!isReadOnly && editMode}
        />

        {/* 日次活動セクション */}
        <DailyActivitySection
          projectId={project.id}
          zones={project.project_zones || []}
          activities={dailyActivities}
          isLoading={loadingActivities}
          error={activitiesError}
          isReadOnly={isReadOnly}
          currentYearMonth={currentYearMonth}
          onYearMonthChange={handleYearMonthChange}
          onResourceEditRequest={handleResourceEditRequest}
          onActivityUpdated={handleActivityUpdated}
          currentUserCraftsmanId={currentUserCraftsmanId}
        />

        {/* 保存ボタン */}
        {!isReadOnly && editMode && (
          <div className="flex justify-end mt-4">
            <Button
              variant="default"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              保存
            </Button>
          </div>
        )}
      </CardContent>

      {/* リソース割り当てポップアップ */}
      {resourcePopupOpen && (
        <ResourceEditPopup
          projectId={project.id}
          date={selectedDate || ''}
          zoneId={selectedZoneId}
          craftsmen={craftsmen || []}
          truckDrivers={truckDrivers || []}
          trucks={trucks || []}
          companyVehicles={companyVehicles || []}
          zones={project.project_zones || []}
          isOpen={resourcePopupOpen}
          onClose={() => setResourcePopupOpen(false)}
          onSaveSuccess={handleActivityUpdated}
          isReadOnly={isReadOnly}
        />
      )}
    </Card>
  );
}; 